import React from 'react';
import {
  type PoiSceneDef,
  POI_SCENE_MAP,
  getSceneKeyByLabel,
  DEFAULT_SCENE_KEY,
} from '../../../../config/poiSceneConfig';

interface PoiLegendItem {
  poiScene?: string;
  poi_scene?: string;
  count: number;
}

interface PoiLegendProps {
  data: PoiLegendItem[];
  visible: boolean;
}

// 读取 poiScene 字段（兼容 API 下划线命名 poi_scene）
const getSceneLabel = (item: PoiLegendItem): string => item.poiScene || item.poi_scene || '';

// 缓存 sceneKey → PoiSceneDef，避免重复查找
const categoryDefCache = new Map<string, PoiSceneDef>();

const getDef = (label?: string): PoiSceneDef => {
  if (!label) return POI_SCENE_MAP[DEFAULT_SCENE_KEY];
  const cached = categoryDefCache.get(label);
  if (cached) return cached;
  const key = getSceneKeyByLabel(label);
  const def = POI_SCENE_MAP[key] ?? POI_SCENE_MAP[DEFAULT_SCENE_KEY];
  categoryDefCache.set(label, def);
  return def;
};

const PoiLegend: React.FC<PoiLegendProps> = ({ data, visible }) => {
  if (!visible || !data || data.length === 0) {
    return null;
  }

  // 按数量降序排列
  const sorted = [...data].sort((a, b) => b.count - a.count);
  const total = sorted.reduce((sum, item) => sum + item.count, 0);

  return (
    <div
      className="absolute bottom-24 right-4 z-[1000] w-[220px] max-w-[240px] animate-in fade-in slide-in-from-bottom-2 duration-200"
    >
      <div className="bg-white/95 backdrop-blur-sm rounded-xl shadow-lg border border-slate-200 overflow-hidden">
        {/* 头部 */}
        <div className="flex items-center justify-between px-3 py-2.5 bg-gradient-to-r from-slate-50 to-white border-b border-slate-200">
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 text-slate-500" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4.25 2A2.25 2.25 0 002 4.25v2.5A2.25 2.25 0 004.25 9h2.5A2.25 2.25 0 009 6.75v-2.5A2.25 2.25 0 006.75 2h-2.5zm0 9A2.25 2.25 0 002 13.25v2.5A2.25 2.25 0 004.25 18h2.5A2.25 2.25 0 009 15.75v-2.5A2.25 2.25 0 006.75 11h-2.5zm9-9A2.25 2.25 0 0011 4.25v2.5A2.25 2.25 0 0013.25 9h2.5A2.25 2.25 0 0018 6.75v-2.5A2.25 2.25 0 0015.75 2h-2.5zm0 9A2.25 2.25 0 0011 13.25v2.5A2.25 2.25 0 0013.25 18h2.5A2.25 2.25 0 0018 15.75v-2.5A2.25 2.25 0 0015.75 11h-2.5z" clipRule="evenodd" />
            </svg>
            <span className="text-xs font-semibold text-slate-700 tracking-wide uppercase">POI 分类统计</span>
          </div>
        </div>

        {/* 列表 */}
        <div className="px-3 py-2 space-y-1.5">
          {sorted.map((item, index) => {
            const label = getSceneLabel(item);
            const def = getDef(label);
            return (
              <div key={label || index} className="flex items-center justify-between group">
                <div className="flex items-center gap-2 min-w-0">
                  <div
                    className={`w-2.5 h-2.5 rounded-full shrink-0 ring-2 ring-offset-1 ${def.bg}`}
                    style={{ '--tw-ring-color': def.color } as React.CSSProperties}
                  />
                  <span className="text-xs text-slate-700 truncate">{label}</span>
                </div>
                <span
                  className={`text-xs font-semibold shrink-0 ml-2 px-1.5 py-0.5 rounded ${def.text} ${def.bg.replace('bg-', 'bg-').replace('-500', '-50')}`}
                >
                  {item.count}
                </span>
              </div>
            );
          })}
        </div>

        {/* 合计 */}
        {sorted.length > 1 && (
          <div className="px-3 py-2 border-t border-slate-100 bg-slate-50/50 flex items-center justify-between">
            <span className="text-xs font-medium text-slate-500">合计</span>
            <span className="text-xs font-bold text-slate-700">{total}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default PoiLegend;
